/** Single-value bars: pillars, utilization, statement coverage, upload progress. */
import { colorVar } from '@/lib/tokens'

const pct = (ratio: number) => `${Math.max(0, Math.min(1, ratio)) * 100}%`

export function TrackBar({
  ratio,
  color,
  label,
}: {
  ratio: number
  color: string
  label: string
}) {
  return (
    <div
      className="track-bar"
      role="img"
      aria-label={label}
      title={label}
    >
      <div className="track-bar-fill" style={{ width: pct(ratio), background: color }} />
    </div>
  )
}

/**
 * `indeterminate` is for work with no reportable percentage — a bank sync, for
 * one. It omits aria-valuenow rather than passing a made-up ratio, so a screen
 * reader announces "busy" instead of a number nothing measured.
 */
export function ProgressBar({
  ratio = 0,
  label,
  indeterminate,
}: {
  ratio?: number
  label: string
  indeterminate?: boolean
}) {
  return (
    <div
      className="progress-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      {...(indeterminate ? { 'aria-busy': true } : { 'aria-valuenow': Math.round(ratio * 100) })}
      aria-label={label}
    >
      <div
        className={`track-bar-fill${indeterminate ? ' track-bar-fill-indeterminate' : ''}`}
        style={{ width: indeterminate ? undefined : pct(ratio), background: colorVar('primary') }}
      />
    </div>
  )
}

export interface CoverageSegment {
  ratio: number
  covered: boolean
}

/** Statement coverage with its holes drawn in. */
export function CoverageBar({ segments, label }: { segments: CoverageSegment[]; label: string }) {
  return (
    <div className="coverage-bar" role="img" aria-label={label} title={label}>
      {segments.map((segment, i) => (
        <span
          key={i}
          style={{
            width: pct(segment.ratio),
            background: segment.covered ? colorVar('primary') : 'transparent',
          }}
        />
      ))}
    </div>
  )
}
